import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { relativeTime, absoluteTime, fileSize } from '../lib/format.js';
import { Page } from '../app/AppShell.js';
import { Modal } from '../components/Modal.js';
import { QrCode } from '../components/QrCode.js';
import { QrScanner } from '../components/QrScanner.js';
import { useToast } from '../components/Toast.js';
import { RowMenu } from '../components/RowMenu.js';
import { ShareWithDemoAdmins } from '../components/ShareWithDemoAdmins.js';
import { DisplayStatusBadge, OnlineDot, Loading, ErrorNote, EmptyState } from '../components/ui.js';
import { useAuth } from '../lib/auth.js';
import { useCapabilities } from '../lib/capabilities.js';
import { LicenseStateBadge } from './Licenses.js';
import { IssueLicenseModal, KeysModal } from './settings/PortableLicenses.js';
import type { CommandType, Display, Layout, PortableLicense } from '../lib/types.js';

const PLAYER_URL_KEY = 'akarya.displays.playerAppUrl';
const LEGACY_PLAYER_URL_KEY = 'akarya.fleet.playerAppUrl';

/** Builds the `?server=&code=` deep link the player's claim screen reads on
 *  boot, or null while `playerUrl` isn't a usable absolute URL yet. */
function enrollLink(playerUrl: string, claimCode: string): string | null {
  let url: URL;
  try {
    url = new URL(playerUrl);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  url.search = '';
  url.searchParams.set('server', api.serverOrigin());
  url.searchParams.set('code', claimCode);
  return url.toString();
}

const COMMANDS: { type: CommandType; label: string }[] = [
  { type: 'screenshot', label: 'Screenshot now' },
  { type: 'resync', label: 'Force resync' },
  { type: 'restartApp', label: 'Restart player' },
  { type: 'clearCache', label: 'Clear cache' },
  { type: 'identify', label: 'Identify' },
  { type: 'reboot', label: 'Reboot device' },
];

/** The portable player app isn't a system app, so it can never reboot the device. */
function commandsFor(display: Display): typeof COMMANDS {
  return display.portablePlayerId ? COMMANDS.filter((c) => c.type !== 'reboot') : COMMANDS;
}

/** `settings.rotation`: how far clockwise the screen is mounted turned; the player turns its content to match. */
const SCREEN_ROTATIONS: { degrees: number; label: string }[] = [
  { degrees: 0, label: 'None' },
  { degrees: 90, label: '90° clockwise' },
  { degrees: 180, label: 'Upside down' },
  { degrees: 270, label: '90° counter-clockwise' },
];

export function DisplaysScreen(): ReactNode {
  const displays = useQuery(() => api.displays(), [], { pollMs: 10000 });
  const layouts = useQuery(() => api.layouts(), []);
  const toast = useToast();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const caps = useCapabilities();
  const canIssueLicenses = caps?.portableLicenses === true && user?.canIssueLicenses === true;
  const licenceSales = caps?.portableLicenseSales === true;
  const simple = caps?.simple === true;

  const [adding, setAdding] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<PortableLicense[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [claim, setClaim] = useState<{ code: string; minutes: number } | null>(null);
  // Admins share items with demo administrators, who otherwise only see what they made.
  const canShare = can('users.manage');
  const [sharing, setSharing] = useState<{ id: string; name: string } | null>(null);
  const [playerUrl, setPlayerUrl] = useState(() => localStorage.getItem(PLAYER_URL_KEY) ?? localStorage.getItem(LEGACY_PLAYER_URL_KEY) ?? '');

  function updatePlayerUrl(value: string): void {
    setPlayerUrl(value);
    localStorage.setItem(PLAYER_URL_KEY, value);
  }

  const selectedDisplay = displays.data?.find((d) => d.id === selected) ?? null;

  async function rowAction(work: () => Promise<unknown>, done: string): Promise<void> {
    try {
      await work();
      toast.push(done, 'success');
      displays.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <Page
      title="Display"
      actions={
        <div className="row">
          {/* A screen showing a pairing QR (spec §2.3) — the quick way for TVs. In a box
              it is the primary action: one tap opens the camera. */}
          <button className={simple ? 'btn primary' : 'btn'} onClick={() => setScanning(true)}>
            {simple ? 'Add screen' : 'Scan screen QR'}
          </button>
          <button className={simple ? 'btn' : 'btn primary'} onClick={() => setAdding(true)}>
            {simple ? 'Add with code' : 'Add display'}
          </button>
          {/* For a portable player that has never connected to a portable CMS, so no
              account has its licence ID yet: the key is pasted in on the player itself. */}
          {canIssueLicenses ? (
            <button className="btn" onClick={() => setIssuing(true)}>
              Generate portable player licence
            </button>
          ) : null}
        </div>
      }
    >
      {scanning ? (
        <QrScanner
          onClose={() => setScanning(false)}
          onCode={(code) => {
            setScanning(false);
            navigate(`/claim?code=${encodeURIComponent(code)}`);
          }}
        />
      ) : null}
      {issuing ? (
        <IssueLicenseModal
          onClose={() => setIssuing(false)}
          onIssued={(licenses) => {
            setIssuing(false);
            setIssued(licenses);
          }}
        />
      ) : null}
      {issued ? <KeysModal licenses={issued} onClose={() => setIssued(null)} /> : null}
      {displays.loading && !displays.data ? <Loading what="displays" /> : null}
      {displays.error ? <ErrorNote error={displays.error} /> : null}

      {displays.data ? (
        displays.data.length === 0 ? (
          <EmptyState>
            No displays yet. <button className="btn small" onClick={() => (simple ? setScanning(true) : setAdding(true))}>Add one</button>
          </EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th />
                  <th>Name</th>
                  <th>Status</th>
                  <th>Layout</th>
                  <th>Device</th>
                  <th>Last seen</th>
                  <th>Faults</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {displays.data.map((d) => (
                  <tr key={d.id} className="clickable" onClick={() => setSelected(d.id)}>
                    <td><OnlineDot lastSeenAt={d.lastSeenAt} /></td>
                    <td>{d.name}</td>
                    <td>
                      <div className="row wrap" style={{ gap: 6 }}>
                        <DisplayStatusBadge status={d.status} />
                        {licenceSales && d.portableLicenseState ? (
                          <LicenseStateBadge state={d.portableLicenseState} />
                        ) : null}
                        {licenceSales &&
                        can('licenses.purchase') &&
                        d.portableLicenseId &&
                        d.portableLicenseState !== 'licensed' ? (
                          <Link
                            className="btn small"
                            to={`/licenses?select=${encodeURIComponent(d.portableLicenseId)}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Buy licence
                          </Link>
                        ) : null}
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <LayoutSelect
                        display={d}
                        layouts={layouts.data ?? []}
                        disabled={!can('displays.manage')}
                        onChange={async (layoutId) => {
                          try {
                            await api.updateDisplay(d.id, { default_layout_id: layoutId });
                            toast.push('Default layout updated', 'success');
                            displays.refetch();
                            return true;
                          } catch (err) {
                            toast.error(err);
                            return false;
                          }
                        }}
                      />
                    </td>
                    <td className="muted small">
                      {[d.model, d.shell].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="muted small" title={absoluteTime(d.lastSeenAt)}>
                      {relativeTime(d.lastSeenAt)}
                    </td>
                    <td>
                      {d.faults.length > 0 ? (
                        <span className="badge bad">{d.faults.length}</span>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row" style={{ gap: 2, justifyContent: 'flex-end' }}>
                        <Link className="btn small ghost" to={`/displays/${d.id}/schedule`}>
                          Schedule
                        </Link>
                        <RowMenu
                          label={`Actions for ${d.name}`}
                          items={[
                            { label: 'View details', onSelect: () => setSelected(d.id) },
                            { label: 'Edit schedule', onSelect: () => navigate(`/displays/${d.id}/schedule`) },
                            canShare && { label: 'Share with demo admins…', onSelect: () => setSharing({ id: d.id, name: d.name }) },
                            {
                              label: 'New claim code',
                              onSelect: () =>
                                void api
                                  .claimCode(d.id)
                                  .then((code) => setClaim({ code: code.claimCode, minutes: code.expiresInMinutes }))
                                  .catch((err: unknown) => toast.error(err)),
                            },
                            ...commandsFor(d).map((c) => ({
                              label: c.label,
                              onSelect: () =>
                                void rowAction(() => api.sendCommand(d.id, c.type), `Queued: ${c.label}`),
                            })),
                            d.status === 'disabled'
                              ? {
                                  label: 'Re-enable',
                                  onSelect: () =>
                                    void rowAction(
                                      () => api.updateDisplay(d.id, { status: 'active' }),
                                      'Display re-enabled',
                                    ),
                                }
                              : {
                                  label: 'Disable',
                                  danger: true,
                                  onSelect: () =>
                                    void rowAction(
                                      () => api.updateDisplay(d.id, { status: 'disabled' }),
                                      'Display disabled',
                                    ),
                                },
                            {
                              label: 'Delete',
                              danger: true,
                              onSelect: () => {
                                if (!confirm(`Delete “${d.name}”? This cannot be undone.`)) return;
                                void rowAction(() => api.deleteDisplay(d.id), 'Display deleted');
                              },
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {adding ? (
        <AddDisplayModal
          onClose={() => setAdding(false)}
          onCreated={(display, code) => {
            setAdding(false);
            displays.refetch();
            setClaim({ code: code.claimCode, minutes: code.expiresInMinutes });
            setSelected(display.id);
          }}
        />
      ) : null}

      {selectedDisplay ? (
        <DisplayDetailModal
          display={selectedDisplay}
          layouts={layouts.data ?? []}
          onClose={() => setSelected(null)}
          onChanged={() => displays.refetch()}
          onClaimCode={(code) => {
            setSelected(null);
            setClaim({ code: code.claimCode, minutes: code.expiresInMinutes });
          }}
        />
      ) : null}

      {/* Rendered last so it stacks above the detail modal, which stays open
          after adding a display. */}
      {claim ? (
        <Modal title="Claim code" onClose={() => setClaim(null)}>
          <p className="muted small">
            Enter this on the new player to enroll it. Expires in {claim.minutes} minutes.
          </p>
          <div
            className="mono"
            style={{ fontSize: 30, letterSpacing: 4, textAlign: 'center', padding: '18px 0' }}
          >
            {claim.code}
          </div>
          <div className="row between">
            <button
              className="btn small"
              onClick={() => {
                void navigator.clipboard?.writeText(claim.code).then(() => toast.push('Copied'));
              }}
            >
              Copy
            </button>
            <button className="btn primary small" onClick={() => setClaim(null)}>
              Done
            </button>
          </div>

          <label className="field" style={{ marginTop: 16 }}>
            Player app URL
            <span className="hint">
              Set once — wherever the web player shell is hosted. Unlocks a QR code / link below
              so you don’t have to type the code on the remote.
            </span>
            <input
              placeholder="https://play.example.com"
              value={playerUrl}
              onChange={(e) => updatePlayerUrl(e.target.value.trim())}
            />
          </label>

          {playerUrl
            ? (() => {
                const link = enrollLink(playerUrl, claim.code);
                if (!link) return <p className="form-error">Not a valid http(s) URL.</p>;
                return (
                  <div className="stack" style={{ alignItems: 'center', marginTop: 12 }}>
                    <QrCode text={link} size={180} />
                    <button
                      className="btn small"
                      onClick={() => {
                        void navigator.clipboard?.writeText(link).then(() => toast.push('Link copied'));
                      }}
                    >
                      Copy link
                    </button>
                    <p className="faint small" style={{ textAlign: 'center' }}>
                      Scans or opens straight into enrollment — no typing on the remote.
                    </p>
                  </div>
                );
              })()
            : null}
        </Modal>
      ) : null}
      {sharing !== null ? (
        <ShareWithDemoAdmins type="display" id={sharing.id} name={sharing.name} onClose={() => setSharing(null)} />
      ) : null}
    </Page>
  );
}

/** A portable CMS player with no cloud default layout plays what its own CMS
 *  app assigns, so the empty choice inherits rather than shows nothing. */
function emptyLayoutLabel(display: Display): string {
  return display.portablePlayerId ? '— inherit —' : '— none —';
}

/** Inline default-layout picker for a display row. Holds the picked value until
 *  the refetched row reflects it, so the select doesn't snap back meanwhile. */
function LayoutSelect({
  display,
  layouts,
  disabled,
  onChange,
}: {
  display: Display;
  layouts: Layout[];
  disabled: boolean;
  /** Resolves false when the update failed. */
  onChange: (layoutId: string | null) => Promise<boolean>;
}): ReactNode {
  const current = display.defaultLayoutId ?? '';
  const [pending, setPending] = useState<string | null>(null);
  if (pending !== null && pending === current) setPending(null);
  const value = pending ?? current;
  const known = value === '' || layouts.some((l) => l.id === value);

  return (
    <select
      className="small"
      aria-label={`Default layout for ${display.name}`}
      value={value}
      disabled={disabled || pending !== null}
      onChange={(e) => {
        const next = e.target.value;
        setPending(next);
        void onChange(next || null).then((ok) => {
          if (!ok) setPending(null);
        });
      }}
    >
      <option value="">{emptyLayoutLabel(display)}</option>
      {/* Keeps the current value selectable when the layouts list hasn't
          loaded or isn't visible to this user. */}
      {!known ? <option value={value}>(current layout)</option> : null}
      {layouts.map((l) => (
        <option key={l.id} value={l.id}>
          {l.name}
          {l.status !== 'published' ? ' (draft)' : ''}
        </option>
      ))}
    </select>
  );
}

function AddDisplayModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (display: Display, code: { claimCode: string; expiresInMinutes: number }) => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function create(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const display = await api.createDisplay(name.trim());
      const code = await api.claimCode(display.id);
      onCreated(display, code);
    } catch (err) {
      setError(err instanceof ApiError ? (err.fieldMessage() ?? err.message) : String(err));
      setBusy(false);
    }
  }

  return (
    <Modal title="Add display" onClose={onClose}>
      <div className="stack">
        <label className="field">
          Name
          <span className="hint">Where the screen lives — “Lobby north wall”, “Cafe menu”.</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        {error ? <div className="form-error">{error}</div> : null}
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || name.trim() === ''} onClick={() => void create()}>
            {busy ? 'Creating…' : 'Create & get claim code'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DisplayDetailModal({
  display,
  layouts,
  onClose,
  onChanged,
  onClaimCode,
}: {
  display: Display;
  layouts: Layout[];
  onClose: () => void;
  onChanged: () => void;
  onClaimCode: (code: { claimCode: string; expiresInMinutes: number }) => void;
}): ReactNode {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [shotNonce, setShotNonce] = useState(() => Date.now());

  async function guarded(work: () => Promise<unknown>, done?: string): Promise<void> {
    setBusy(true);
    try {
      await work();
      if (done) toast.push(done, 'success');
      onChanged();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={display.name} onClose={onClose} wide>
      <div className="stack">
        <div className="row wrap">
          <DisplayStatusBadge status={display.status} />
          <span className="badge flat">{display.credentialMode ?? 'not enrolled'}</span>
          <span className="badge flat">{display.orientation}</span>
          {display.currentScheduleId ? (
            <span className="badge flat mono">{display.currentScheduleId}</span>
          ) : null}
        </div>

        <div className="row wrap small muted">
          <span>Last seen {relativeTime(display.lastSeenAt)}</span>
          <span>·</span>
          <span>Last sync {relativeTime(display.lastSyncAt)}</span>
          <span>·</span>
          <span>Cache {fileSize(display.cacheBytesUsed)}</span>
        </div>

        {display.faults.length > 0 ? (
          <div className="card card-pad">
            <strong className="small">Faults</strong>
            <ul className="small muted" style={{ margin: '6px 0 0' }}>
              {display.faults.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="editor halves">
          <div className="stack">
            <label className="field">
              Default layout
              <select
                value={display.defaultLayoutId ?? ''}
                disabled={busy}
                onChange={(e) =>
                  void guarded(
                    () =>
                      api.updateDisplay(display.id, {
                        default_layout_id: e.target.value || null,
                      }),
                    'Default layout updated',
                  )
                }
              >
                <option value="">{emptyLayoutLabel(display)}</option>
                {layouts.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {l.status !== 'published' ? ' (draft)' : ''}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              Screen rotation
              <select
                value={String(display.settings?.rotation ?? 0)}
                disabled={busy}
                onChange={(e) =>
                  void guarded(
                    () =>
                      // Settings are replaced as a whole, so keep the others.
                      api.updateDisplay(display.id, {
                        settings: { ...(display.settings ?? {}), rotation: Number(e.target.value) },
                      }),
                    'Screen rotation updated',
                  )
                }
              >
                {SCREEN_ROTATIONS.map((r) => (
                  <option key={r.degrees} value={r.degrees}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="row wrap">
              {display.status === 'disabled' ? (
                <button
                  className="btn small"
                  disabled={busy}
                  onClick={() =>
                    void guarded(
                      () => api.updateDisplay(display.id, { status: 'active' }),
                      'Display re-enabled',
                    )
                  }
                >
                  Re-enable
                </button>
              ) : (
                <button
                  className="btn small danger"
                  disabled={busy}
                  onClick={() =>
                    void guarded(
                      () => api.updateDisplay(display.id, { status: 'disabled' }),
                      'Display disabled',
                    )
                  }
                >
                  Disable
                </button>
              )}
              <button
                className="btn small"
                disabled={busy}
                onClick={() =>
                  void guarded(async () => {
                    onClaimCode(await api.claimCode(display.id));
                  })
                }
              >
                New claim code
              </button>
              <button
                className="btn small danger"
                disabled={busy}
                onClick={() => {
                  if (!confirm(`Delete “${display.name}”? This cannot be undone.`)) return;
                  void guarded(() => api.deleteDisplay(display.id), 'Display deleted').then(onClose);
                }}
              >
                Delete
              </button>
            </div>

            <div>
              <strong className="small">Send command</strong>
              <div className="row wrap" style={{ marginTop: 6 }}>
                {commandsFor(display).map((c) => (
                  <button
                    key={c.type}
                    className="btn small"
                    disabled={busy}
                    onClick={() =>
                      void guarded(async () => {
                        await api.sendCommand(display.id, c.type);
                      }, `Queued: ${c.label}`)
                    }
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <p className="faint small" style={{ marginTop: 6 }}>
                Commands are delivered on the display’s next poll.
              </p>
            </div>
          </div>

          <div className="preview-frame" style={{ position: 'static' }}>
            <div className="row between" style={{ marginBottom: 8 }}>
              <strong className="small">Last screenshot</strong>
              <button className="btn small ghost" onClick={() => setShotNonce(Date.now())}>
                Refresh
              </button>
            </div>
            {display.screenshotAt ? (
              <img
                src={`${api.screenshotUrl(display.id)}?t=${shotNonce}`}
                alt="Last screenshot"
                style={{ width: '100%', borderRadius: 6, display: 'block' }}
              />
            ) : (
              <p className="faint small">
                None yet. Use “Screenshot now”, then refresh after the next poll.
              </p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
